import { motion } from "framer-motion";
import { Clock, ArrowUpRight, Calendar } from "lucide-react";
import { BlogPost } from "@/data/blogData";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { blogCardCopy } from "@/lib/siteCopy";

interface BlogCardProps {
  post: BlogPost;
  index?: number;
  variant?: "default" | "featured" | "compact";
  onClick?: () => void;
  className?: string;
}

export function BlogCard({
  post,
  index = 0,
  variant = "default",
  onClick,
  className,
}: BlogCardProps) {
  const lang = useSiteLanguage();
  const t = blogCardCopy[lang];
  const dateLocale = lang === "hi" ? "hi-IN" : "en-US";
  const formattedDate = new Date(post.publishedAt).toLocaleDateString(
    dateLocale,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
  );

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: "easeOut" as const,
      },
    },
  };

  const imageVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.4, ease: "easeOut" as const },
    },
  };

  const arrowVariants = {
    initial: { x: 0, y: 0 },
    hover: {
      x: 4,
      y: -4,
      transition: { duration: 0.2, ease: "easeOut" as const },
    },
  };

  if (variant === "compact") {
    return (
      <motion.article
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        whileHover="hover"
        onClick={onClick}
        className={cn(
          "group cursor-pointer flex gap-4 p-4 rounded-xl",
          "border border-border hover:border-foreground/20",
          "bg-card hover:bg-accent/50",
          "transition-all duration-300",
          className,
        )}
      >
        <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <motion.img
            src={post.coverImage}
            alt={post.title}
            variants={imageVariants}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-foreground transition-colors">
            {post.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span>{formattedDate}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {post.readTime} {t.min}
            </span>
          </div>
        </div>
      </motion.article>
    );
  }

  if (variant === "featured") {
    return (
      <motion.article
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        whileHover="hover"
        onClick={onClick}
        className={cn(
          "group cursor-pointer relative rounded-2xl overflow-hidden",
          "border border-border hover:border-foreground/20",
          "bg-card shadow-sm hover:shadow-xl",
          "transition-all duration-500",
          className,
        )}
      >
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden">
          <motion.img
            src={post.coverImage}
            alt={post.title}
            variants={imageVariants}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

          {/* Category Badge */}
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 text-xs font-medium bg-white/90 dark:bg-black/70 text-foreground rounded-full backdrop-blur-sm">
              {post.category}
            </span>
          </div>

          {/* Arrow Icon */}
          <motion.div
            variants={arrowVariants}
            className="absolute top-4 right-4 p-2 bg-white/90 dark:bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ArrowUpRight className="w-4 h-4" />
          </motion.div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formattedDate}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTime} {t.minRead}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-foreground transition-colors">
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
            {post.excerpt}
          </p>

          {/* Author */}
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <img
              src={post.author.avatar}
              alt={post.author.name}
              className="w-8 h-8 rounded-full object-cover"
              loading="lazy"
            />
            <div>
              <p className="text-sm font-medium">{post.author.name}</p>
              <p className="text-xs text-muted-foreground">
                {post.author.role}
              </p>
            </div>
          </div>
        </div>
      </motion.article>
    );
  }

  // Default variant
  return (
    <motion.article
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      whileHover="hover"
      onClick={onClick}
      className={cn(
        "group cursor-pointer rounded-xl overflow-hidden",
        "border border-border hover:border-foreground/20",
        "bg-card shadow-sm hover:shadow-lg",
        "transition-all duration-300",
        className,
      )}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <motion.img
          src={post.coverImage}
          alt={post.title}
          variants={imageVariants}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 text-xs font-medium bg-white/90 dark:bg-black/70 text-foreground rounded-full backdrop-blur-sm">
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2.5">
          <span>{formattedDate}</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {post.readTime} {t.min}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-foreground transition-colors">
          {post.title}
        </h3>

        {/* Excerpt */}
        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
          {post.excerpt}
        </p>

        {/* Read More */}
        <div className="flex items-center text-sm font-medium text-foreground group-hover:gap-2 transition-all">
          <span>{t.readMore}</span>
          <motion.span variants={arrowVariants}>
            <ArrowUpRight className="w-4 h-4 ml-1" />
          </motion.span>
        </div>
      </div>
    </motion.article>
  );
}
